<div class="phone-numbers-container">
  <!-- Header -->
  <div class="phone-numbers-header">
    <div class="title-section">
      <h1 i18n="@@phoneNumbers.title">Phone numbers</h1>
      <p class="subtitle" i18n="@@phoneNumbers.subtitle">Import and manage your phone numbers</p>
    </div>
    <div>
      <p-button
        #importButton
        i18n-label="@@phoneNumbers.import"
        label="Import number"
        icon="pi pi-plus"
        (click)="importMenu.toggle($event)">
      </p-button>
      <p-menu
        #importMenu
        [model]="importMenuItems"
        [popup]="true"
        [appendTo]="'body'"
        styleClass="import-number-menu">
        <ng-template pTemplate="item" let-item>
          <a class="p-menuitem-link" (click)="item.command()">
            @if (item.iconUrl) {
              <img [src]="item.iconUrl" class="menu-icon" />
            }
            @if (item.icon && !item.iconUrl) {
              <i [class]="item.icon"></i>
            }
            <span class="p-menuitem-text">{{ item.label }}</span>
          </a>
        </ng-template>
      </p-menu>
    </div>
  </div>

  <!-- Custom SIP Accounts (shown even with 0 phone numbers, so user can manage them) -->
  @if (!isLoading && customAccounts.length > 0) {
    <div class="custom-accounts-section">
      <div class="section-header">
        <h2 i18n="@@phoneNumbers.customAccounts.title">Custom SIP accounts</h2>
        <p class="section-subtitle" i18n="@@phoneNumbers.customAccounts.subtitle">
          Self-hosted SIP trunks. Click an account to verify, reprovision, edit or delete.
        </p>
      </div>
      <div class="custom-accounts-grid">
        @for (acct of customAccounts; track acct.id) {
          <button type="button" class="custom-account-card" (click)="openAccount(acct)">
            <div class="card-icon"><i class="pi pi-server"></i></div>
            <div class="card-body">
              <div class="card-title">{{ acct.account_identifier || '—' }}</div>
              <div class="card-meta">{{ customAccountSubtitle(acct) }}</div>
            </div>
            <i class="pi pi-chevron-right card-chevron"></i>
          </button>
        }
      </div>
    </div>
  }

  <!-- Loading State -->
  @if (isLoading) {
    <div class="loading-container">
      <p-progressSpinner />
      <p i18n="@@phoneNumbers.loading">Loading phone numbers...</p>
    </div>
  } @else if (phoneNumbers.length > 0) {
    <!-- Desktop Phone Numbers Table -->
    <div class="table-container desktop-table">
      <p-table
        [value]="phoneNumbers"
        [tableStyle]="{ 'min-width': '50rem' }"
        styleClass="phone-numbers-table">
        <ng-template pTemplate="header">
          <tr>
            <th i18n="@@phoneNumbers.col.label">Label</th>
            <th i18n="@@phoneNumbers.col.phone">Phone number</th>
            <th i18n="@@phoneNumbers.col.agent">Assigned agent</th>
            <th i18n="@@phoneNumbers.col.provider">Provider</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-phoneNumber>
          <tr (click)="editPhoneNumber(phoneNumber)" class="clickable-row">
            <td>{{ phoneNumber.label || '-' }}</td>
            <td>
              <div class="phone-number-cell">
                <span>{{ phoneNumber.phone_number || '-' }}</span>
                @if (phoneNumber.phone_number) {
                  <p-button
                    icon="pi pi-copy"
                    [text]="true"
                    size="small"
                    severity="secondary"
                    i18n-pTooltip="@@phoneNumbers.copy"
                    pTooltip="Copy phone number"
                    tooltipPosition="top"
                    (click)="copyPhoneNumber(phoneNumber.phone_number); $event.stopPropagation()"
                    i18n-ariaLabel="@@phoneNumbers.copy"
                    ariaLabel="Copy phone number">
                  </p-button>
                }
              </div>
            </td>
            <td>
              @if (phoneNumber.agent_title) {
                <a
                  class="agent-link"
                  (click)="navigateToAgent(phoneNumber.agent_id); $event.stopPropagation()"
                  i18n-pTooltip="@@phoneNumbers.viewAgent"
                  pTooltip="View agent"
                  tooltipPosition="top">
                  {{ phoneNumber.agent_title }}
                  <i class="pi pi-external-link"></i>
                </a>
              } @else {
                <span class="no-agent">-</span>
              }
            </td>
            <td>{{ getProviderForPhoneNumber(phoneNumber) }}</td>
            <td (click)="$event.stopPropagation()">
              <div class="action-buttons">
                <p-button
                  icon="pi pi-pencil"
                  [text]="true"
                  severity="secondary"
                  size="small"
                  i18n-pTooltip="@@common.edit"
                  pTooltip="Edit"
                  tooltipPosition="top"
                  (click)="editPhoneNumber(phoneNumber)"
                  i18n-ariaLabel="@@common.edit"
                  ariaLabel="Edit">
                </p-button>
                <p-button
                  icon="pi pi-trash"
                  [text]="true"
                  severity="danger"
                  size="small"
                  i18n-pTooltip="@@common.delete"
                  pTooltip="Delete"
                  tooltipPosition="top"
                  (click)="deletePhoneNumber(phoneNumber)"
                  i18n-ariaLabel="@@common.delete"
                  ariaLabel="Delete">
                </p-button>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- Mobile Phone Numbers Table -->
    <div class="table-container mobile-table">
      <p-table
        [value]="phoneNumbers"
        styleClass="phone-numbers-table-mobile">
        <ng-template pTemplate="header">
          <tr>
            <th i18n="@@phoneNumbers.col.phone">Phone number</th>
            <th i18n="@@phoneNumbers.col.provider">Provider</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-phoneNumber let-ri="rowIndex">
          <tr (click)="editPhoneNumber(phoneNumber)" class="clickable-row">
            <td>
              <div class="phone-number-cell">
                <span>{{ phoneNumber.phone_number || '-' }}</span>
                @if (phoneNumber.phone_number) {
                  <p-button
                    icon="pi pi-copy"
                    [text]="true"
                    size="small"
                    severity="secondary"
                    (click)="copyPhoneNumber(phoneNumber.phone_number); $event.stopPropagation()">
                  </p-button>
                }
              </div>
            </td>
            <td>{{ getProviderForPhoneNumber(phoneNumber) }}</td>
            <td (click)="$event.stopPropagation()">
              <p-button
                icon="pi pi-ellipsis-v"
                [text]="true"
                severity="secondary"
                size="small"
                (click)="mobileActionMenu.toggle($event)"
                [attr.data-row-index]="ri">
              </p-button>
              <p-menu
                #mobileActionMenu
                [model]="getMobileMenuItems(phoneNumber)"
                [popup]="true"
                [appendTo]="'body'">
              </p-menu>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  } @else {
    <!-- Empty State -->
    <div class="empty-state">
      <i class="pi pi-phone"></i>
      <p i18n="@@phoneNumbers.empty">No phone numbers imported yet</p>
      <p-button
        #emptyImportButton
        i18n-label="@@phoneNumbers.importFirst"
        label="Import your first number"
        icon="pi pi-plus"
        (click)="importMenu.toggle($event)">
      </p-button>
    </div>
  }
</div>

<!-- Import Twilio Number Drawer -->
<app-import-twilio-number
  [(visible)]="importDrawerVisible"
  [accounts]="accounts"
  (imported)="onImported()">
</app-import-twilio-number>

<!-- Import Voximplant Number Drawer -->
<app-import-voximplant-number
  [(visible)]="importVoximplantDrawerVisible"
  [accounts]="accounts"
  (imported)="onImported()">
</app-import-voximplant-number>

<!-- Import Custom SIP Number Drawer -->
<app-import-custom-sip-number
  [visible]="importCustomSipDrawerVisible"
  (visibleChange)="onCustomSipDrawerVisibleChange($event)"
  [accounts]="accounts"
  (imported)="onImported()"
  (accountCreated)="onImported()">
</app-import-custom-sip-number>

<!-- Delete Confirmation Dialog -->
<app-delete-confirmation
  [(visible)]="deleteDialogVisible"
  [title]="deleteTitle"
  [subtitle]="deleteSubtitle"
  [deleting]="isDeleting"
  (confirm)="onDeleteConfirm()"
  (cancel)="onDeleteCancel()" />
